import type { UserRole } from "bancojs";

/**
 * Las secciones de la app y el rol que exige cada una.
 *
 * Los roles salen de los @PreAuthorize de los controladores de la API. Esta
 * tabla es la que alimenta a la vez los guards del router, los `v-if` de los
 * drawers y las tarjetas del Gateway, para que las tres cosas no puedan
 * discrepar.
 */
export interface Section {
  /** Prefijo de ruta, p. ej. "/admin/users". */
  path: string;
  /** Etiqueta del menú. */
  label: string;
  icon: string;
  /** Alcanza con tener CUALQUIERA de estos roles. */
  roles: readonly UserRole[];
}

/** Portal de administración — AdminXController. */
export const ADMIN_SECTIONS: readonly Section[] = [
  {
    path: "/admin/users",
    label: "Usuarios",
    icon: "mdi-account-group",
    roles: ["ADMIN", "ADMIN_USERS"],
  },
  {
    path: "/admin/clients",
    label: "Clientes",
    icon: "mdi-apps",
    roles: ["ADMIN", "ADMIN_CLIENTS"],
  },
  {
    path: "/admin/keys",
    label: "Llaves",
    icon: "mdi-key",
    roles: ["ADMIN", "ADMIN_KEYS"],
  },
  {
    path: "/admin/certificates",
    label: "Certificados",
    icon: "mdi-certificate",
    roles: ["ADMIN", "ADMIN_CERTIFICATES"],
  },
  {
    path: "/admin/mappers",
    label: "Mapeadores",
    icon: "mdi-swap-horizontal",
    // OJO: SetupDataLoader no crea ADMIN_CERTIFICATES_MAPPERS. Sin sembrarlo por
    // fuera, sólo un ADMIN puro llega acá.
    roles: ["ADMIN", "ADMIN_CERTIFICATES_MAPPERS"],
  },
] as const;

/** Portal de sistema — SystemController. */
export const SYSTEM_SECTIONS: readonly Section[] = [
  {
    path: "/system/keys",
    label: "Llaves de sistema",
    icon: "mdi-key-chain-variant",
    roles: ["ADMIN", "ADMIN_SYSTEM"],
  },
] as const;

/** Portal de usuario — sólo requiere estar registrado y activo. */
export const MY_SECTIONS: readonly Section[] = [
  { path: "/my/profile", label: "Mi Perfil", icon: "mdi-account", roles: [] },
  {
    path: "/my/certificates",
    label: "Mis Certificados",
    icon: "mdi-certificate-outline",
    roles: [],
  },
] as const;

/**
 * La primera sección accesible de un grupo, o null.
 *
 * Es lo que reemplaza a los redirects fijos: `/admin` apuntaba siempre a
 * `/admin/users`, lo que daba 403 a un usuario con sólo ADMIN_KEYS.
 */
export function firstAccessible(
  sections: readonly Section[],
  canAccess: (roles: readonly UserRole[]) => boolean
): string | null {
  return sections.find((s) => canAccess(s.roles))?.path ?? null;
}
